import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, CircleNotch, Info, Sparkle } from '@phosphor-icons/react';
import {
  useGetBrandProfile,
  useUpdateBrandProfile,
} from '@reelplicate/api-client/brand-profiles';
import { type BrandProfileDto, OnboardingStep } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Button, cn, Field, Stack, TagInput, TextInput, Well } from '@reelplicate/ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { siteHost } from './api-values';
import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import { BrandStepSchema, type BrandStepValues } from './schemas';
import { ONBOARDING_STEPS, stepPath } from './steps';

const EMPTY: BrandStepValues = {
  product: '',
  audience: '',
  offer: '',
  tone: [],
  approvedClaims: [],
  prohibited: [],
};

// Poll while the site is being read; give up after this and let them type.
const POLL_MS = 1_500;
const GIVE_UP_MS = 45_000;

const toValues = (p: BrandProfileDto): BrandStepValues => ({
  product: p.product ?? '',
  audience: p.audience ?? '',
  offer: p.offer ?? '',
  tone: p.tone,
  approvedClaims: p.approvedClaims,
  prohibited: p.prohibited,
});

/** The Button's loading spinner, sized by the Well. */
function Spinner({ className }: { className?: string }) {
  return (
    <CircleNotch
      aria-hidden
      className={cn(
        'animate-spin [animation-duration:800ms] motion-reduce:[animation-duration:1600ms]',
        className,
      )}
    />
  );
}

const FAILURE_COPY: Record<string, string> = {
  disabled: 'Reading websites isn’t switched on yet - fill these in yourself.',
  rate_limited: 'We’ve read a lot of sites for you this hour - fill these in yourself.',
  blocked: 'That address isn’t one we can read - fill these in yourself.',
};

/**
 * The note above the fields: what's happening with the website read. Reading
 * → spinner; READY (not yet edited) → "Filled from {host}"; failed or timed
 * out → a neutral note to fill in by hand. Nothing when there's no website.
 */
function ReadStatus({ profile, gaveUp }: { profile?: BrandProfileDto; gaveUp: boolean }) {
  if (!profile || profile.status === 'EMPTY') return null;
  const host = siteHost(profile.sourceUrl);
  const reading = profile.status === 'PENDING' || profile.status === 'READING';
  if (reading && !gaveUp) {
    return (
      <Well tone="brand" icon={Spinner} title={`Reading ${host}…`} role="status">
        This takes a few seconds - you can start typing meanwhile.
      </Well>
    );
  }
  if (profile.status === 'READY') {
    if (profile.editedAt) return null;
    return (
      <Well tone="brand" icon={Sparkle} title={`Filled from ${host}`}>
        Check it over and edit anything that’s off.
      </Well>
    );
  }
  return (
    <Well icon={Info} title={`We couldn’t read ${host}`} role="status">
      {FAILURE_COPY[profile.failureReason ?? ''] ??
        'Fill these in yourself - you can change them any time.'}
    </Well>
  );
}

/**
 * Figma "Onboarding — 2 · Brand" (126:548): a brand Well saying the fields
 * came from the website, brand / audience side by side, offer, then a Tag
 * Input for tone. 14px between fields. (Approved claims / prohibited
 * language are commented out for now.)
 *
 * The fields prefill from the brand profile read from the step 1 website -
 * polled while the read runs; values that arrive never replace what the user
 * has already typed. Continue saves the profile as theirs.
 */
export function BrandStepPage() {
  const navigate = useNavigate();
  const form = useForm<BrandStepValues>({
    resolver: zodResolver(BrandStepSchema),
    defaultValues: EMPTY,
  });
  const { advance } = useOnboardingProgress();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id;
  const [startedAt] = useState(() => Date.now());
  const [gaveUp, setGaveUp] = useState(false);
  const { data: profile } = useGetBrandProfile(workspaceId ?? '', {
    query: {
      enabled: Boolean(workspaceId),
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status !== 'PENDING' && status !== 'READING') return false;
        return Date.now() - startedAt < GIVE_UP_MS ? POLL_MS : false;
      },
    },
  });
  const save = useUpdateBrandProfile();

  const reading = profile?.status === 'PENDING' || profile?.status === 'READING';
  useEffect(() => {
    if (!reading) return;
    const timer = setTimeout(() => setGaveUp(true), GIVE_UP_MS);
    return () => clearTimeout(timer);
  }, [reading]);

  // Fill in what arrives, keeping anything the user already typed.
  useEffect(() => {
    if (profile && (profile.status === 'READY' || profile.editedAt)) {
      form.reset(toValues(profile), { keepDirtyValues: true });
    }
  }, [profile, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (workspaceId) {
      try {
        await save.mutateAsync({
          id: workspaceId,
          data: {
            product: values.product,
            audience: values.audience,
            offer: values.offer || null,
            tone: values.tone,
            approvedClaims: values.approvedClaims,
            prohibited: values.prohibited,
          },
        });
      } catch {
        form.setError('root', { message: 'Couldn’t save your brand. Try again.' });
        return;
      }
    }
    if (await advance(OnboardingStep.COMPETITORS)) await navigate(stepPath(3));
  });

  return (
    <OnboardingCard onSubmit={onSubmit}>
      <OnboardingHeader
        eyebrow={`Step 2 of ${ONBOARDING_STEPS.length}`}
        title="Tell us about your brand"
        subtitle="Generated content reads from this — the more you give it, the closer the first drafts land."
      />

      <Stack className="gap-3.5">
        <ReadStatus profile={profile} gaveUp={gaveUp} />
        <Stack gap={3} className="tablet:flex-row tablet:items-start">
          <Controller
            name="product"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Brand or product"
                error={fieldState.error?.message}
                className="flex-1"
              >
                <TextInput {...field} placeholder="e.g. Oat & Ember — barista oat milk" />
              </Field>
            )}
          />
          <Controller
            name="audience"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Audience"
                error={fieldState.error?.message}
                className="flex-1"
              >
                <TextInput {...field} placeholder="e.g. Busy parents who love coffee" />
              </Field>
            )}
          />
        </Stack>
        <Controller
          name="offer"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field label="Offer" error={fieldState.error?.message}>
              <TextInput {...field} placeholder="e.g. 20% off your first order" />
            </Field>
          )}
        />
        <Controller
          name="tone"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field label="Tone" error={fieldState.error?.message}>
              <TagInput
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Add a word"
              />
            </Field>
          )}
        />
        {/* Approved claims and prohibited language: hidden for now, restore by
            uncommenting (the form still carries their values).
        <Stack gap={3} className="tablet:flex-row tablet:items-start">
          <Controller
            name="approvedClaims"
            control={form.control}
            render={({ field }) => (
              <Field label="Approved claims" className="flex-1">
                <TagInput
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Add a claim"
                />
              </Field>
            )}
          />
          <Controller
            name="prohibited"
            control={form.control}
            render={({ field }) => (
              <Field label="Prohibited language" className="flex-1">
                <TagInput
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="Add a phrase"
                />
              </Field>
            )}
          />
        </Stack>
        */}
      </Stack>

      {form.formState.errors.root && (
        <p role="alert" className="text-body-sm text-status-danger-fg">
          {form.formState.errors.root.message}
        </p>
      )}

      <OnboardingActions back={stepPath(1)}>
        <Button
          type="submit"
          size="lg"
          trailingIcon={ArrowRight}
          loading={form.formState.isSubmitting}
        >
          Continue
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
